import { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { CONVERSATIONS_QUERY, CONVERSATION_QUERY } from '../../graphql';
import { ChatHistoryItem, NEW_CONVERSATION_PLACEHOLDER } from '../Interface';
import { groupConversationsByDate, groupMessagesIntoRequests } from './chatUtils';
import { Logger } from 'shared-components/utils';
import { safeLocalStorageGetItem, safeLocalStorageRemoveItem, safeLocalStorageSetItem } from './safeLocalStorage';

const logger = new Logger('useChatHistory');

// Persist selected conversation ID per patient
const STORAGE_KEY_PREFIX = 'ask-selected-conversation:';

const CONVERSATION_NOT_FOUND_PATTERNS = [/^Conversation(\s+\d+)? not found$/i];
export const getConversationSelectionStorageKey = (patientId: string) => `${STORAGE_KEY_PREFIX}${patientId}`;

// Parse conversation id from storage
const parseStoredConversationId = (value: string | null): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

/**
 * Pure query hook for fetching conversations list
 */
export const useConversationsQuery = (patientId: string) => {
  const parsedPatientId = parseInt(patientId, 10);
  return useQuery(CONVERSATIONS_QUERY, {
    variables: { patientId: parsedPatientId },
    skip: !patientId || Number.isNaN(parsedPatientId),
    // Handle errors locally to prevent global redirect on 403
    errorPolicy: 'all',
    context: {
      locallyHandledErrors: ['Conversation not found', 'Conversation does not belong to the specified patient'],
    },
    onError: (error) => {
      logger.error('useConversationsQuery error', error.message);
    },
  });
};

/**
 * Pure query hook for fetching a single conversation
 */
export const useConversationQuery = (conversationId: number | null) => {
  return useQuery(CONVERSATION_QUERY, {
    variables: { conversationId },
    skip: !conversationId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true, // Notify when network status changes to detect background refetches
    // Handle errors locally to prevent global redirect on 403
    errorPolicy: 'all',
    context: {
      locallyHandledErrors: ['Conversation not found', 'Conversation does not belong to the specified patient'],
    },
    onError: (error) => {
      logger.error('useConversationQuery error', error.message);
    },
  });
};

/**
 * Unified hook to manage chat history state and data fetching
 * Owns all state and coordinates between queries and UI state
 */
export const useChatHistory = (
  patientId: string,
  timezone: string,
  setValue: (index: number) => void,
): {
  chatHistory: ChatHistoryItem[];
  setChatHistory: (updater: (prev: ChatHistoryItem[]) => ChatHistoryItem[]) => void;
  selectedConversationId: number | null;
  setSelectedConversationId: (id: number | null) => void;
  conversationsLoading: boolean;
  isInitialising: boolean;
  isLoadingConversation: boolean;
  refetchConversations: () => void;
} => {
  // Data fetching hooks
  const {
    data: conversationsData,
    loading: conversationsLoading,
    refetch: refetchConversations,
  } = useConversationsQuery(patientId);

  // True until the conversations list has resolved at least once for this patient.
  // Stays false on subsequent refetches (e.g. after creating a conversation) so we
  // don't flash a full loading state when only the list is updating.
  const [isInitialising, setIsInitialising] = useState<boolean>(() => !!patientId && conversationsLoading);

  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const [isSwitchingConversation, setIsSwitchingConversation] = useState(false);
  const hasInitialisedSelectionRef = useRef(false);
  const shouldSkipInitialPersistRef = useRef(false);
  const previousPatientIdRef = useRef<string | null>(null);
  const {
    data: conversationData,
    loading: conversationLoading,
    networkStatus,
    error: conversationError,
  } = useConversationQuery(selectedConversationId);

  // State ownership: chatHistory is managed here
  const [chatHistory, setChatHistory] = useState<ChatHistoryItem[]>([NEW_CONVERSATION_PLACEHOLDER]);

  // Flip out of the initial loading state once the first list fetch completes
  // (success or error). We deliberately don't reset to true on later refetches.
  useEffect(() => {
    if (!patientId) {
      setIsInitialising(false);
      return;
    }
    if (!conversationsLoading) {
      setIsInitialising(false);
    }
  }, [patientId, conversationsLoading]);

  useEffect(() => {
    // Clear prior patient selection on switch. Do NOT restore from storage here -
    // wait for conversations list to load so we can validate the stored ID exists.
    if (!patientId) {
      setSelectedConversationId(null);
      shouldSkipInitialPersistRef.current = true;
      hasInitialisedSelectionRef.current = false;
      return;
    }

    const previousPatientId = previousPatientIdRef.current;
    if (previousPatientId && previousPatientId !== patientId) {
      safeLocalStorageRemoveItem(getConversationSelectionStorageKey(previousPatientId));
      hasInitialisedSelectionRef.current = false;
      setSelectedConversationId(null);
    }

    previousPatientIdRef.current = patientId;
  }, [patientId]);

  // Restore selection from storage only after conversations list loads - validates stored ID exists
  // to avoid "Conversation not found" errors from stale/deleted IDs
  useEffect(() => {
    if (!patientId || conversationsLoading || !conversationsData?.conversations || hasInitialisedSelectionRef.current)
      return;

    const storedValue = safeLocalStorageGetItem(getConversationSelectionStorageKey(patientId));
    const storedId = parseStoredConversationId(storedValue);

    if (storedId) {
      const exists = conversationsData.conversations.some(
        (conversation: { id: number }) => conversation.id === storedId,
      );
      if (exists) {
        setSelectedConversationId(storedId);
      } else {
        setSelectedConversationId(null);
        safeLocalStorageRemoveItem(getConversationSelectionStorageKey(patientId));
      }
    }

    hasInitialisedSelectionRef.current = true;
    shouldSkipInitialPersistRef.current = true;
  }, [patientId, conversationsLoading, conversationsData?.conversations]);

  useEffect(() => {
    // Persist selection after initial load so popover/chat stay consistent.
    if (!patientId || !hasInitialisedSelectionRef.current) return;
    if (shouldSkipInitialPersistRef.current) {
      shouldSkipInitialPersistRef.current = false;
      return;
    }

    if (selectedConversationId) {
      safeLocalStorageSetItem(getConversationSelectionStorageKey(patientId), String(selectedConversationId));
    } else {
      safeLocalStorageRemoveItem(getConversationSelectionStorageKey(patientId));
    }
  }, [patientId, selectedConversationId]);

  // Track when conversation is switched to show loading immediately
  // Keep it true until we have actual message data loaded
  useEffect(() => {
    if (selectedConversationId !== null) {
      // Check if conversation already has messages loaded in chatHistory
      const conversationInHistory = chatHistory.find((item) => item.conversationId === selectedConversationId);
      const hasMessages =
        conversationInHistory && conversationInHistory.requests && conversationInHistory.requests.length > 0;

      if (hasMessages) {
        // Already has data - no need to show switching state
        setIsSwitchingConversation(false);
      } else {
        // No data yet - show switching state
        setIsSwitchingConversation(true);
      }
    } else {
      setIsSwitchingConversation(false);
    }
  }, [selectedConversationId, chatHistory]);

  // Load conversations list when data is available, preserving existing messages
  useEffect(() => {
    if (conversationsData?.conversations) {
      setChatHistory((prevChatHistory) => {
        // Preserve all existing conversations with conversationId AND their current state (requests, etc.)
        const existingConversationsMap = new Map(
          prevChatHistory.filter((item) => item.conversationId).map((item) => [item.conversationId!, item]),
        );

        const loadedConversations = groupConversationsByDate(
          conversationsData.conversations,
          timezone,
          existingConversationsMap,
        );

        // Only keep conversations without IDs (in progress) - conversations with IDs come from backend
        // Exclude index 0 (New Conversation placeholder)
        const conversationInProgress = prevChatHistory.find(
          (item, index) => !item.conversationId && item.requests && item.requests.length > 0 && index !== 0,
        );

        return conversationInProgress
          ? [NEW_CONVERSATION_PLACEHOLDER, conversationInProgress, ...loadedConversations]
          : [NEW_CONVERSATION_PLACEHOLDER, ...loadedConversations];
      });
    }
  }, [conversationsData, timezone]);

  useEffect(() => {
    // Clear selection if it is no longer valid
    if (!conversationsData?.conversations || selectedConversationId === null || !patientId) return;

    const existsInHistory = chatHistory.some((item) => item.conversationId === selectedConversationId);
    if (existsInHistory) return;

    const stillExists = conversationsData.conversations.some(
      (conversation: { id: number }) => conversation.id === selectedConversationId,
    );
    if (!stillExists) {
      setSelectedConversationId(null);
      safeLocalStorageRemoveItem(getConversationSelectionStorageKey(patientId));
    }
  }, [conversationsData, selectedConversationId, patientId, chatHistory]);

  // Update tab index when selectedConversationId changes
  const conversationIndex = useMemo(() => {
    return selectedConversationId
      ? chatHistory.findIndex((item) => item.conversationId === selectedConversationId)
      : -1;
  }, [selectedConversationId, chatHistory]);

  // Clear selection when conversation query returns "not found" (e.g. deleted between list load and fetch)
  useEffect(() => {
    if (!conversationError || !selectedConversationId || !patientId) return;

    const messages = [
      conversationError.message,
      ...(conversationError.graphQLErrors?.map((e) => e.message) ?? []),
    ].filter(Boolean) as string[];
    const isNotFound = messages.some((msg) =>
      CONVERSATION_NOT_FOUND_PATTERNS.some((pattern) => pattern.test(msg.trim())),
    );

    if (isNotFound) {
      logger.debug('clearSelectionOnNotFound', 'Conversation not found, clearing selection', selectedConversationId);
      setSelectedConversationId(null);
      safeLocalStorageRemoveItem(getConversationSelectionStorageKey(patientId));
    }
  }, [conversationError, selectedConversationId, patientId]);

  // Load messages when conversation data is available
  useEffect(() => {
    if (conversationData?.conversation && selectedConversationId) {
      try {
        const conversation = conversationData.conversation;
        const messages = conversation.messages || [];
        const requests = groupMessagesIntoRequests(messages, selectedConversationId);

        setChatHistory((prev) => {
          const updated = [...prev];
          const idx = updated.findIndex((item) => item.conversationId === selectedConversationId);

          if (idx !== -1) {
            const existingConversation = updated[idx];
            updated[idx] = {
              ...existingConversation,
              title: conversation.title,
              // Server is source of truth for requests once we have an ID
              requests,
            };
            // Update tab index after conversation data is loaded
            setValue(idx);
          } else {
            const newConversation: ChatHistoryItem = {
              title: conversation.title,
              conversationId: conversation.id,
              requests,
              kind: 'conversation',
            };
            updated.push(newConversation);
            setValue(updated.length - 1);
          }

          return updated;
        });
      } catch (error) {
        // Error handling: log and continue silently to prevent UI disruption
        logger.error('Error loading conversation', error);
      }
    }
  }, [conversationData, selectedConversationId, setValue]);

  // Update tab index when conversation is found in chatHistory (for conversations already loaded)
  // This handles the case where conversation data is already available (e.g., from previous load)
  useEffect(() => {
    if (conversationIndex !== -1 && selectedConversationId !== null) {
      const conversation = chatHistory[conversationIndex];
      // Only update tab index if conversation has been loaded (requests array exists, even if empty)
      // and we're not currently loading new conversation data
      // This prevents switching to incomplete conversation data (placeholders from list don't have requests)
      if (conversation && Array.isArray(conversation.requests) && !conversationLoading) {
        setValue(conversationIndex);
      }
    } else if (selectedConversationId === null) {
      // No conversation selected, go to "New Conversation"
      setValue(0);
    }
  }, [conversationIndex, selectedConversationId, chatHistory, conversationLoading, setValue]);

  // Check if we're fetching (loading) or refetching (networkStatus 4 = refetch)
  // networkStatus 1 = loading, 4 = refetch (cache-and-network background fetch)
  // Also show loading when switching conversations to provide immediate feedback
  const isLoadingConversation = conversationLoading || networkStatus === 4 || isSwitchingConversation;

  return {
    chatHistory,
    setChatHistory,
    selectedConversationId,
    setSelectedConversationId,
    conversationsLoading,
    isInitialising,
    isLoadingConversation,
    refetchConversations,
  };
};

/**
 * Hook to manage delayed refetch for title updates
 */
export const useDelayedRefetch = (
  refetchConversations?: () => void,
  delay: number = 3000,
): ((id: number | null) => void) => {
  const [pendingTitleUpdateConversationId, setPendingTitleUpdateConversationId] = useState<number | null>(null);

  useEffect(() => {
    if (pendingTitleUpdateConversationId && refetchConversations) {
      const timeoutId = setTimeout(() => {
        refetchConversations();
        setPendingTitleUpdateConversationId(null);
      }, delay);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [pendingTitleUpdateConversationId, refetchConversations, delay]);

  return setPendingTitleUpdateConversationId;
};
